
import { CategoryCard } from "./CategoryCard";
import { IntegrationCard } from "./IntegrationCard";
import { useIntegrationStatus } from "./hooks/useIntegrationStatus";
import { Bot, CloudLightning, Gem, Briefcase, Users, Calendar, Mail, Wrench } from "lucide-react";

const CATEGORIES = [
  {
    id: 'ai',
    name: 'AI Services',
    icon: Bot,
    platforms: ['openai', 'anthropic', 'gemini', 'elevenlabs']
  },
  {
    id: 'recruiting',
    name: 'Recruiting Platforms',
    icon: Users,
    platforms: ['linkedin', 'indeed']
  },
  {
    id: 'communication',
    name: 'Email Integration',
    icon: Mail,
    platforms: ['outlook-email']
  },
  {
    id: 'calendar',
    name: 'Calendar & Scheduling',
    icon: Calendar,
    platforms: ['microsoft365', 'google-calendar']
  },
  {
    id: 'tools',
    name: 'Tools & Utilities',
    icon: Wrench,
    platforms: ['resend', 'scrapeless']
  }
];

const AI_PLATFORMS = [
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT models for AI-powered conversations and analysis',
    icon: <Bot className="h-8 w-8 text-green-600" />
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Claude AI models for advanced reasoning and analysis',
    icon: <CloudLightning className="h-8 w-8 text-orange-600" />
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    description: 'Google\'s advanced AI models for multimodal tasks',
    icon: <Gem className="h-8 w-8 text-blue-600" />
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    description: 'AI voice generation and speech synthesis',
    icon: <Bot className="h-8 w-8 text-purple-600" />
  }
];

const RECRUITING_PLATFORMS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    description: 'Professional networking and candidate sourcing',
    icon: <Users className="h-8 w-8 text-blue-700" />
  },
  {
    id: 'indeed',
    name: 'Indeed',
    description: 'Job board integration for candidate sourcing',
    icon: <Briefcase className="h-8 w-8 text-blue-600" />
  }
];

const COMMUNICATION_PLATFORMS = [
  {
    id: 'outlook-email',
    name: 'Outlook Email',
    description: 'Sync emails from your Outlook account',
    icon: <Mail className="h-8 w-8 text-blue-600" />
  }
];

const CALENDAR_PLATFORMS = [
  {
    id: 'microsoft365',
    name: 'Microsoft 365',
    description: 'Calendar integration with Microsoft 365',
    icon: <Calendar className="h-8 w-8 text-blue-600" />
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Calendar integration and scheduling',
    icon: <Calendar className="h-8 w-8 text-green-600" />
  }
];

const TOOLS_PLATFORMS = [
  {
    id: 'resend',
    name: 'Resend',
    description: 'Email delivery service for transactional emails',
    icon: <Mail className="h-8 w-8 text-red-600" />
  },
  {
    id: 'scrapeless',
    name: 'Scrapeless',
    description: 'Web scraping and data enrichment service',
    icon: <Wrench className="h-8 w-8 text-gray-600" />
  }
];

export const IntegrationsGrouping = () => {
  const { 
    isLoading, 
    testingConnection, 
    testConnection, 
    getPlatformStatus, 
    getConnectionSource,
    secretsConfigured
  } = useIntegrationStatus();

  const renderCategorySection = (
    categoryIndex: number,
    platforms: any[],
    title: string
  ) => (
    <div key={title}>
      <CategoryCard
        category={CATEGORIES[categoryIndex]}
        isLoading={isLoading}
        testingConnection={testingConnection}
        getPlatformStatus={getPlatformStatus}
        onTest={testConnection}
        secretsConfigured={secretsConfigured}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {platforms.map((platform) => (
          <IntegrationCard
            key={platform.id}
            id={platform.id}
            name={platform.name}
            description={platform.description}
            icon={platform.icon}
            status={getPlatformStatus(platform.id)}
            isLoading={isLoading}
            isTesting={testingConnection === platform.id}
            onTest={() => testConnection(platform.id)}
            connectionSource={getConnectionSource(platform.id)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* AI Services Category */}
      {renderCategorySection(0, AI_PLATFORMS, "AI Services")}

      {/* Recruiting Platforms Category */}
      {renderCategorySection(1, RECRUITING_PLATFORMS, "Recruiting Platforms")}

      {/* Email Integration Category */}
      {renderCategorySection(2, COMMUNICATION_PLATFORMS, "Email Integration")}

      {/* Calendar & Scheduling Category */}
      {renderCategorySection(3, CALENDAR_PLATFORMS, "Calendar & Scheduling")}

      {/* Tools & Utilities Category */}
      {renderCategorySection(4, TOOLS_PLATFORMS, "Tools & Utilities")}
    </div>
  );
};
